import { useEffect, useState } from "react";
import { CalendarDays, Trash2, Building2, Loader2, Ticket, MapPin, CheckCircle2, AlertTriangle, Phone, Wallet, Send, FileBarChart, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AdminEventsPage() {
  const { organizer } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [working, setWorking] = useState(null);
  const [confirmEvent, setConfirmEvent] = useState(null);
  const [confirmResetAi, setConfirmResetAi] = useState(null);

  const load = () => {
    api
      .get("/admin/events")
      .then((res) => setEvents(res.data))
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const sendVippsGuide = async (ev) => {
    setWorking(ev.id + "guide");
    try {
      const res = await api.post(`/admin/organizers/${ev.organizer_id}/send-vipps-guide`, { event_id: ev.id });
      toast.success(`Vipps-guide sendt til ${res.data.to}`);
      const stamp = new Date().toISOString();
      setEvents((prev) => prev.map((e) => (e.organizer_id === ev.organizer_id ? { ...e, org_vipps_guide_sent_at: stamp } : e)));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking(null);
    }
  };

  const sendSummary = async (ev) => {
    setWorking(ev.id + "summary");
    try {
      const res = await api.post(`/admin/events/${ev.id}/send-summary`);
      toast.success(`Oppsummering sendt til ${ev.org_name}: ${res.data.tickets_sold} billetter, ${res.data.checkin_pct} % innsjekk`);
      setEvents((prev) => prev.map((e) => (e.id === ev.id ? { ...e, summary_sent_at: new Date().toISOString() } : e)));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking(null);
    }
  };

  const resetPosterAi = async () => {
    if (!confirmResetAi) return;
    const ev = confirmResetAi;
    setConfirmResetAi(null);
    setWorking(ev.id + "ai");
    try {
      const res = await api.post(`/admin/events/${ev.id}/poster-ai/reset`);
      toast.success(`AI-forslag nullstilt for «${ev.title}»: ${res.data.deleted} forslag slettet – ${res.data.remaining} av ${res.data.max} tilgjengelig igjen`);
      setEvents((prev) => prev.map((e) => (e.id === ev.id ? { ...e, poster_ai_used: 0 } : e)));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmEvent) return;
    const ev = confirmEvent;
    setConfirmEvent(null);
    setDeleting(ev.id);
    try {
      await api.delete(`/admin/events/${ev.id}`);
      setEvents((prev) => prev.filter((e) => e.id !== ev.id));
      toast.success(`«${ev.title}» er slettet med alle billetter og ordrer`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setDeleting(null);
    }
  };

  if (organizer && !organizer.is_admin) {
    return (
      <DashboardLayout>
        <p className="text-muted-foreground" data-testid="admin-events-denied">Denne siden krever plattform-administrator.</p>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl space-y-6" data-testid="admin-events-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight flex items-center gap-2">
            <CalendarDays className="w-7 h-7" /> Arrangementer (admin)
          </h1>
          <p className="text-muted-foreground mt-1">
            Alle arrangementer på plattformen, uansett arrangør. Sletting fjerner også billetter, ordrer, venteliste, rabattkoder og ubrukte bilder.
          </p>
        </div>

        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="h-24 w-full rounded-xl" />
          </div>
        ) : events.length === 0 ? (
          <Card className="border-border">
            <CardContent className="py-10 text-center text-muted-foreground" data-testid="admin-events-empty">
              Ingen arrangementer i databasen.
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-sm text-muted-foreground" data-testid="admin-events-count">
              {events.length} arrangement{events.length === 1 ? "" : "er"} totalt
            </p>
            {events.map((ev) => (
              <Card key={ev.id} className="border-border" data-testid={`admin-event-${ev.id}`}>
                <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="font-heading font-semibold flex flex-wrap items-center gap-2">
                      <span className="truncate">{ev.title}</span>
                      <Badge variant={ev.published ? "default" : "secondary"}>
                        {ev.published ? "Publisert" : "Kladd"}
                      </Badge>
                      <Badge variant="outline" data-testid={`admin-event-price-${ev.id}`}>
                        {ev.is_paid ? `Betalt · opptil ${ev.max_price} kr` : "Gratis"}
                      </Badge>
                      {!ev.published && (
                        ev.ready_to_publish ? (
                          <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" data-testid={`admin-event-ready-${ev.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Klar til publisering
                          </Badge>
                        ) : (
                          <Badge className="bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))]" data-testid={`admin-event-blocked-${ev.id}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" /> {ev.blockers?.length || 0} hindring{(ev.blockers?.length || 0) === 1 ? "" : "er"}
                          </Badge>
                        )
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{formatDateTime(ev.starts_at)}</span>
                      {(ev.venue_name || ev.city) && (
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{[ev.venue_name, ev.city].filter(Boolean).join(", ")}</span>
                      )}
                      <span className="flex items-center gap-1"><Ticket className="w-3.5 h-3.5" />{ev.tickets} billetter · {ev.orders} ordrer</span>
                    </p>
                    <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{ev.org_name} · {ev.org_email}</span>
                      {ev.org_phone && (
                        <a href={`tel:+47${ev.org_phone}`} className="flex items-center gap-1 underline underline-offset-4" data-testid={`admin-event-phone-${ev.id}`}>
                          <Phone className="w-3.5 h-3.5" />{ev.org_phone}
                        </a>
                      )}
                      {ev.is_paid && (
                        <span className="flex items-center gap-1" data-testid={`admin-event-vipps-${ev.id}`}>
                          <Wallet className="w-3.5 h-3.5" />
                          {ev.org_vipps_configured ? `Vipps ${ev.org_vipps_env === "production" ? "produksjon" : "test"}` : "Vipps mangler – reservasjon åpen"}
                        </span>
                      )}
                    </p>
                    {!ev.published && ev.blockers?.length > 0 && (
                      <ul className="text-xs text-[hsl(var(--warning-foreground))] bg-[hsl(var(--warning))]/15 border border-[hsl(var(--warning))]/40 rounded-md px-3 py-2 space-y-0.5 list-disc list-inside" data-testid={`admin-event-blockers-${ev.id}`}>
                        {ev.blockers.map((b) => <li key={b}>{b}</li>)}
                      </ul>
                    )}
                    {ev.warnings?.length > 0 && (
                      <ul className="text-xs text-muted-foreground bg-muted/60 border border-border rounded-md px-3 py-2 space-y-0.5 list-disc list-inside" data-testid={`admin-event-warnings-${ev.id}`}>
                        {ev.warnings.map((w) => <li key={w}>{w}</li>)}
                      </ul>
                    )}
                  </div>
                  <div className="shrink-0 flex flex-col items-stretch gap-2">
                    {ev.is_paid && !ev.org_vipps_configured && (
                      <div className="flex flex-col items-end gap-1">
                        <Button
                          size="sm"
                          onClick={() => sendVippsGuide(ev)}
                          disabled={!!working}
                          data-testid={`admin-send-vipps-guide-${ev.id}`}
                        >
                          {working === ev.id + "guide" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <><Send className="w-4 h-4 mr-1" />Send Vipps-guide</>
                          )}
                        </Button>
                        {ev.org_vipps_guide_sent_at && (
                          <span className="text-[11px] text-muted-foreground" data-testid={`admin-vipps-guide-sent-${ev.id}`}>
                            Sendt {formatDateTime(ev.org_vipps_guide_sent_at)}
                          </span>
                        )}
                      </div>
                    )}
                    {ev.is_past && ev.published && (
                      <div className="flex flex-col items-end gap-1">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => sendSummary(ev)}
                          disabled={!!working}
                          data-testid={`admin-send-summary-${ev.id}`}
                        >
                          {working === ev.id + "summary" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <><FileBarChart className="w-4 h-4 mr-1" />{ev.summary_sent_at ? "Send oppsummering igjen" : "Send oppsummering"}</>
                          )}
                        </Button>
                        {ev.summary_sent_at && (
                          <span className="text-[11px] text-muted-foreground" data-testid={`admin-summary-sent-${ev.id}`}>
                            Sendt {formatDateTime(ev.summary_sent_at)}
                          </span>
                        )}
                      </div>
                    )}
                    {ev.poster_ai_used > 0 && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setConfirmResetAi(ev)}
                        disabled={!!working || !!deleting}
                        data-testid={`admin-reset-poster-ai-${ev.id}`}
                      >
                        {working === ev.id + "ai" ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><RotateCcw className="w-4 h-4 mr-1" />Nullstill AI-forslag ({ev.poster_ai_used}/{ev.poster_ai_max} brukt)</>
                        )}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      className="text-[hsl(var(--danger))]"
                      onClick={() => setConfirmEvent(ev)}
                      disabled={!!deleting || !!working}
                      data-testid={`admin-delete-event-${ev.id}`}
                    >
                      {deleting === ev.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Trash2 className="w-4 h-4 mr-1" />Slett</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}

        <AlertDialog open={!!confirmEvent} onOpenChange={(open) => !open && setConfirmEvent(null)}>
          <AlertDialogContent data-testid="admin-delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Slette «{confirmEvent?.title}»?</AlertDialogTitle>
              <AlertDialogDescription>
                Dette sletter arrangementet permanent, sammen med {confirmEvent?.tickets ?? 0} billetter og {confirmEvent?.orders ?? 0} ordrer.
                Arrangør: {confirmEvent?.org_name}. Handlingen kan ikke angres.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="admin-delete-cancel">Avbryt</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90"
                data-testid="admin-delete-confirm"
              >
                Slett permanent
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!confirmResetAi} onOpenChange={(open) => !open && setConfirmResetAi(null)}>
          <AlertDialogContent data-testid="admin-reset-poster-ai-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Nullstille AI-forslag for «{confirmResetAi?.title}»?</AlertDialogTitle>
              <AlertDialogDescription>
                De {confirmResetAi?.poster_ai_used ?? 0} genererte AI-forslagene slettes, og arrangøren får igjen alle {confirmResetAi?.poster_ai_max ?? 3} forslag tilgjengelig.
                Plakaten bruker vanlig stil til et nytt AI-forslag er laget. Brukte OpenAI-kreditter refunderes ikke.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="admin-reset-poster-ai-cancel">Avbryt</AlertDialogCancel>
              <AlertDialogAction onClick={resetPosterAi} data-testid="admin-reset-poster-ai-confirm">
                Nullstill
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
